/**
 * 字体优化工具
 * 解决字体加载2.5秒的问题
 */

const fontsPath =
  process.env.NODE_ENV === 'production' ? `${process.env.PUBLIC_URL}/static` : '../assets';
/**
 * 预加载关键字体
 * 在HTML head中添加preload链接
 */
export const preloadCriticalFonts = () => {
  if (typeof document === 'undefined') return;
  // 只预加载实际使用的3个字体
  const criticalFonts = [
    {
      href: `${fontsPath}/fonts/SourceSansPro-Regular.otf`,
      weight: '400',
      style: 'normal',
    },
    {
      href: `${fontsPath}/fonts/SourceSansPro-Semibold.otf`,
      weight: '600',
      style: 'normal',
    },
    {
      href: `${fontsPath}/fonts/SourceSansPro-Bold.otf`,
      weight: '700',
      style: 'normal',
    },
  ];

  criticalFonts.forEach((font) => {
    // 检查是否已经添加
    const existingLink = document.querySelector(`link[href="${font.href}"]`);
    if (existingLink) return;

    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'font';
    link.type = 'font/otf';
    link.href = font.href;
    link.crossOrigin = 'anonymous';

    // 添加到head的最前面，确保优先加载
    const firstChild = document.head.firstChild;
    if (firstChild) {
      document.head.insertBefore(link, firstChild);
    } else {
      document.head.appendChild(link);
    }
  });
};

/**
 * 使用font-display: swap 策略
 * 确保文本立即可见（使用系统字体）
 * 字体加载完成后再替换
 */
export const ensureFontDisplaySwap = () => {
  if (typeof document === 'undefined') return;

  // 添加CSS确保font-display生效
  const style = document.createElement('style');
  style.textContent = `
    /* 确保所有字体使用swap策略 */
    @font-face {
      font-display: swap;
    }
    
    /* 字体加载时使用系统字体作为fallback */
    body {
      font-family: 'Source Sans Pro', -apple-system, BlinkMacSystemFont, 
        'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;
    }
  `;
  document.head.appendChild(style);
};

/**
 * 延迟加载次要字体
 * 5秒后加载不常用的字重和斜体
 */
export const loadSecondaryFonts = () => {
  if (typeof document === 'undefined') return;

  // 5秒后加载次要字体
  setTimeout(() => {
    const secondaryFonts = `
      /* Light (300) */
      @font-face {
        font-family: 'Source Sans Pro';
        src: url('${fontsPath}/fonts/SourceSansPro-Light.otf') format('opentype');
        font-weight: 300;
        font-style: normal;
        font-display: swap;
      }
      
      /* Light Italic */
      @font-face {
        font-family: 'Source Sans Pro';
        src: url('${fontsPath}/fonts/SourceSansPro-LightIt.otf') format('opentype');
        font-weight: 300;
        font-style: italic;
        font-display: swap;
      }
      
      /* ExtraLight (200) */
      @font-face {
        font-family: 'Source Sans Pro';
        src: url('${fontsPath}/fonts/SourceSansPro-ExtraLight.otf') format('opentype');
        font-weight: 200;
        font-style: normal;
        font-display: swap;
      }
      
      /* ExtraLight Italic */
      @font-face {
        font-family: 'Source Sans Pro';
        src: url('${fontsPath}/fonts/SourceSansPro-ExtraLightIt.otf') format('opentype');
        font-weight: 200;
        font-style: italic;
        font-display: swap;
      }
      
      /* Regular Italic */
      @font-face {
        font-family: 'Source Sans Pro';
        src: url('${fontsPath}/fonts/SourceSansPro-It.otf') format('opentype');
        font-weight: 400;
        font-style: italic;
        font-display: swap;
      }
      
      /* Semibold Italic */
      @font-face {
        font-family: 'Source Sans Pro';
        src: url('${fontsPath}/fonts/SourceSansPro-SemiboldIt.otf') format('opentype');
        font-weight: 600;
        font-style: italic;
        font-display: swap;
      }
      
      /* Bold Italic */
      @font-face {
        font-family: 'Source Sans Pro';
        src: url('${fontsPath}/fonts/SourceSansPro-BoldIt.otf') format('opentype');
        font-weight: 700;
        font-style: italic;
        font-display: swap;
      }
      
      /* Black (900) */
      @font-face {
        font-family: 'Source Sans Pro';
        src: url('${fontsPath}/fonts/SourceSansPro-Black.otf') format('opentype');
        font-weight: 900;
        font-style: normal;
        font-display: swap;
      }
      
      /* Black Italic */
      @font-face {
        font-family: 'Source Sans Pro';
        src: url('${fontsPath}/fonts/SourceSansPro-BlackIt.otf') format('opentype');
        font-weight: 900;
        font-style: italic;
        font-display: swap;
      }
    `;

    // 动态添加样式
    const style = document.createElement('style');
    style.id = 'secondary-fonts';
    style.textContent = secondaryFonts;
    document.head.appendChild(style);

    console.log('🔤 次要字体已延迟加载(5秒后)');
  }, 5000); // 5秒延迟
};

/**
 * 监控字体加载性能
 */
export const monitorFontLoading = () => {
  if (typeof window === 'undefined' || !('fonts' in document)) return;

  // 使用Font Loading API监控
  document.fonts.ready.then(() => {
    const loadTime = performance.now();
    console.log(`✅ 关键字体加载完成: ${Math.round(loadTime)}ms`);
  });

  // 监控每个字体的加载
  if (document.fonts.forEach) {
    document.fonts.forEach((font) => {
      font.loaded
        .then(() => {
          console.log(`✅ 字体已加载: ${font.family} ${font.weight} ${font.style}`);
        })
        .catch((error) => {
          console.warn(`⚠️ 字体加载失败: ${font.family}`, error);
        });
    });
  }
};

/**
 * 初始化字体优化
 */
export const initFontOptimization = () => {
  if (typeof window === 'undefined') return;

  console.log('🔤 启动字体优化...');

  try {
    // 1. 预加载关键字体（最高优先级）
    preloadCriticalFonts();

    // 2. 确保font-display策略
    ensureFontDisplaySwap();

    // 3. 延迟加载次要字体（5秒后）
    loadSecondaryFonts();

    // 4. 监控字体加载（开发环境）
    if (process.env.NODE_ENV === 'development') {
      monitorFontLoading();
    }

    console.log('✅ 字体优化已启动（关键字体立即加载，次要字体5秒后加载）');
  } catch (error) {
    console.warn('字体优化失败:', error);
  }
};
